// GbCoreBase — everything the browser (./web.ts) and headless
// (./headless.ts) adapters share. Both wrap the same vendored
// roblouie/gameboy-emulator core; the tier adapters add only their
// genuinely tier-specific surface (BGP/OBP + tilemap introspection
// for the devtools panel, RAM snapshots for the test harness).
//
// Notable bits of the adapter:
//
//   - Audio is push-based: the APU calls `onSampleBatch(mono)` as it
//     produces samples. We accumulate mono → interleaved stereo.
//   - The vendor renders RGBA bytes into `gpu.displayImageData`; we
//     convert to the canonical 0x00BBGGRR u32 layout once per frame.
//   - There's no side-effect-free peek API, but bus.readByte is
//     side-effect-free on this core for every range a Munos script
//     reads. Writes are clamped to Work RAM ($C000–$DFFF); everything
//     else is dropped (Munos scripts shouldn't poke hardware
//     registers).
//   - OAM-DMA detection wraps bus.writeByte and filters to $FF46.
import { Gameboy } from "./vendor/gameboy.js";
import { captureGbState, applyGbState } from "./savestate.js";
import { assertSaveStateCore, CpuReg, } from "@multinostalgia/core-contract";
// Registry id this core's savestates are tagged with — matches the
// `id` exported by both tier adapters (./web.ts, ./headless.ts).
const CORE_ID = "gb-roblouie";
const SCREEN_W = 160;
const SCREEN_H = 144;
// DMG shade table (0=white, 3=black) in 0x00BBGGRR. Matches the GPU's
// internal `colors` array so palette readers + framebuffer renders stay
// visually consistent.
const SHADE_TABLE = [0x00ffffff, 0x00c0c0c0, 0x00606060, 0x00000000];
// NTSC Game Boy runs at 4.194304 MHz / 70224 cycles per frame
// (~59.7275 fps).
const CYCLES_PER_FRAME = 70224;
// CPU clock (Hz) — converts a VGM sample count to APU cycles for the
// standalone synth path (apu.tick is cycle-driven).
const GB_CPU_HZ = 4194304;
// Ceiling on the undrained audio buffer. The host drains every frame in normal
// use, but a consumer that enables sound and never drains would otherwise grow
// audioAccum without bound. ~5s of interleaved stereo @48kHz — past it we drop
// new batches rather than accumulate forever.
const MAX_AUDIO_ACCUM_SAMPLES = 480_000;
// The chip this core synthesizes (AudioSynthCapable). Frozen singleton.
const GB_CHIPS = Object.freeze(["gb-dmg"]);
// Button id → input flag setter. Matches the BaseCore numbering
// (A=0 .. RIGHT=7 — see core-contract/headless-core.ts comment).
// GB happens to have the same eight buttons as NES in the same order.
const BUTTON_KEYS = [
    "isPressingA", "isPressingB", "isPressingSelect", "isPressingStart",
    "isPressingUp", "isPressingDown", "isPressingLeft", "isPressingRight",
];
export class GbCoreBase {
    system = "gb";
    screenWidth = SCREEN_W;
    screenHeight = SCREEN_H;
    // 4194304 Hz CPU / 70224 cycles per frame = 59.7275 fps (the DMG's true
    // rate). Pacing this at a flat 60 fps overproduces audio and eventually
    // overflows the output ring → crackle; see BaseCore.frameRateHz.
    frameRateHz = 4194304 / 70224;
    framebuffer = new Uint32Array(SCREEN_W * SCREEN_H);
    gb;
    renderPixels;
    sampleRate;
    audioAccum = [];
    oamDmaHandler = null;
    // Observer for the game's APU-register writes (AudioCaptureCapable) —
    // taps the same bus.writeByte wrapper the OAM-DMA detector uses.
    audioCaptureHandler = null;
    // Set once the APU has been wired for standalone synth use — see
    // synthSamples / AudioSynthCapable.
    synthReady = false;
    // ----- execution-hook state (ExecHookCapable) -----
    //
    // Mirrors the NES core (packages/cores/nes-nesjs). The per-instruction
    // trap (cpu.onExecute) is installed only while ≥1 hook is armed; the
    // armed-offset sets give an O(1) "does this PC match?" check so the
    // common (no-hit) path is a Set.has, not a map walk. ROM_BANK hooks key
    // off the bank-stable flat ROM offset; CPU hooks off the raw address.
    execHooks = new Map();
    execHookNextId = 1;
    armedRomOffsets = new Set();
    armedCpuAddrs = new Set();
    execTrapInstalled = false;
    constructor(opts) {
        this.gb = new Gameboy();
        this.renderPixels = opts.renderPixels;
        this.sampleRate = opts.sampleRate;
        if (opts.emulateSound) {
            this.gb.apu.sampleRate = opts.sampleRate;
            this.gb.apu.enableSound();
            // Accumulate APU sample batches; drainAudio() hands them out as
            // interleaved stereo (the GB APU is mono, so L=R).
            this.gb.apu.onSampleBatch = (mono) => {
                if (this.audioAccum.length >= MAX_AUDIO_ACCUM_SAMPLES)
                    return;
                for (let i = 0; i < mono.length; i++) {
                    const s = mono[i];
                    this.audioAccum.push(s, s);
                }
            };
        }
        this.installOamDmaHook();
    }
    // Wrap bus.writeByte so writes to the GB's OAM-DMA register ($FF46)
    // fire our handler after the original handler completes the 160-byte
    // OAM copy. Installed once here in the constructor; it survives
    // loadROM because the bus reference doesn't change. (loadROM must
    // NOT re-install — that would double-wrap writeByte and the handler
    // would fire twice per DMA.)
    installOamDmaHook() {
        const bus = this.gb.bus;
        const original = bus.writeByte.bind(bus);
        bus.writeByte = (addr, val) => {
            original(addr, val);
            if (addr === 0xff46) {
                const h = this.oamDmaHandler;
                if (h)
                    h();
            }
            // APU register writes (NRxx $FF10-$FF26 + wave RAM $FF30-$FF3F)
            // feed the capture observer when one is armed.
            const cap = this.audioCaptureHandler;
            if (cap && ((addr >= 0xff10 && addr <= 0xff26) || (addr >= 0xff30 && addr <= 0xff3f))) {
                cap(addr - 0xff10, val & 0xff);
            }
        };
    }
    // ----- lifecycle -----
    loadROM(bytes) {
        // Gameboy.loadGame wants an ArrayBuffer; the Uint8Array we receive
        // may be a view onto a larger buffer (Node's readFileSync returns
        // Buffer = Uint8Array over a shared pool), so slice to an isolated
        // ArrayBuffer the cartridge can hold onto safely.
        const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
        this.gb.loadGame(ab);
        this.gb.initialize();
    }
    frame() {
        this.gb.runCycles(CYCLES_PER_FRAME);
        // Flush the APU's partial sample batch so this frame's audio is
        // available to drainAudio() now, instead of being quantized to the
        // 1024-sample batch boundary (a GB frame is ~800 samples).
        if (this.gb.apu.isAudioEnabled)
            this.gb.apu.flushSampleBatch();
        // blitFramebuffer is a pure RGBA conversion of the GPU's already-
        // rendered image — no emulation state depends on it, so skipping it
        // when no consumer reads the framebuffer is safe.
        if (this.renderPixels)
            this.blitFramebuffer();
    }
    // Convert the vendor's RGBA byte framebuffer into our 0x00BBGGRR
    // Uint32Array layout. One pixel per loop iteration; for 160x144 =
    // 23040 pixels this is hot but tractable (~25µs at 4ns/op).
    blitFramebuffer() {
        const src = this.gb.gpu.displayImageData.data;
        const dst = this.framebuffer;
        for (let i = 0, j = 0; i < dst.length; i++, j += 4) {
            const r = src[j];
            const g = src[j + 1];
            const b = src[j + 2];
            dst[i] = ((b << 16) | (g << 8) | r) >>> 0;
        }
    }
    // ----- input -----
    buttonDown(controller, button) {
        if (controller !== 1)
            return; // GB is single-controller
        const key = BUTTON_KEYS[button & 0x07];
        if (key)
            this.gb.input[key] = true;
    }
    buttonUp(controller, button) {
        if (controller !== 1)
            return;
        const key = BUTTON_KEYS[button & 0x07];
        if (key)
            this.gb.input[key] = false;
    }
    // ----- audio -----
    drainAudio() {
        if (this.audioAccum.length === 0)
            return null;
        const out = new Float32Array(this.audioAccum);
        this.audioAccum.length = 0;
        return out;
    }
    // ----- audio synthesis (AudioSynthCapable) -----
    //
    // Drive the DMG APU as a standalone synthesizer with no ROM and no CPU:
    // a VGM player writes registers ($FF10-$FF26 + wave RAM $FF30-$FF3F) via
    // writeAudioRegister and pulls samples via synthSamples → apu.tick. The
    // memory bus routes APU-range writes straight to the channel registers
    // independent of any cartridge, so no game is needed.
    audioChips() {
        return GB_CHIPS;
    }
    writeAudioRegister(reg, value) {
        this.ensureSynth();
        // reg is the offset from $FF10 (0x00-0x16 = NRxx, 0x20-0x2f = wave RAM).
        this.gb.bus.writeByte((0xff10 + (reg & 0xff)) & 0xffff, value & 0xff);
    }
    synthSamples(samples) {
        this.ensureSynth();
        // apu.tick() emits at most ONE sample per call (it samples once when
        // its cycle counter crosses the per-sample threshold), so it must be
        // driven in small steps the way the CPU drives it — one tick of
        // ~cyclesPerSample per output sample, letting the APU's internal
        // fractional counter absorb the rounding.
        const perSample = Math.max(1, Math.round(GB_CPU_HZ / this.sampleRate));
        for (let i = 0; i < samples; i++) {
            this.gb.apu.tick(perSample);
        }
        // Hand out this batch's tail immediately so a per-frame synth measure
        // sees every sample it generated rather than losing the sub-1024
        // remainder until the next call.
        this.gb.apu.flushSampleBatch();
    }
    // Wire the APU for synth output if the constructor didn't already (it
    // only does so when emulateSound was true). A synth instance is built
    // with emulateSound: true, so this is usually a no-op.
    ensureSynth() {
        if (this.synthReady)
            return;
        if (!this.gb.apu.isAudioEnabled) {
            this.gb.apu.sampleRate = this.sampleRate;
            this.gb.apu.enableSound();
            this.gb.apu.onSampleBatch = (mono) => {
                if (this.audioAccum.length >= MAX_AUDIO_ACCUM_SAMPLES)
                    return;
                for (let i = 0; i < mono.length; i++) {
                    const s = mono[i];
                    this.audioAccum.push(s, s);
                }
            };
        }
        this.synthReady = true;
    }
    // ----- audio capture (AudioCaptureCapable) -----
    onAudioRegisterWrite(handler) {
        this.audioCaptureHandler = handler;
    }
    // VGM capture priming (see AudioCaptureCapable.audioPrimeState) is
    // NES-only for v1 — the DMG path has no register-snapshot wired up yet,
    // so a capture started mid-sound on the GB carries the same "channel
    // enabled before Record" gap the NES path now closes. Returns [] (no
    // prime); revisit when GB sound capture gets real use.
    audioPrimeState() {
        return [];
    }
    // ----- execution hooks (ExecHookCapable) -----
    //
    // The GB twin of the NES core's exec-hook surface, backing the Munos
    // on_exec / write_rom / read_reg / write_reg builtins. Code lives in
    // ROM ($0000-$7FFF), so coordinates are PRG_ROM flat offsets (built by
    // bank()/prg() in the script); CPU coordinates are the raw-address
    // escape hatch. See docs/execution-hooks-and-bank-switching.md.
    onExecHook(target, handler, priority = 0) {
        const id = this.execHookNextId++;
        this.execHooks.set(id, { region: target.region, offset: target.offset, handler, priority });
        if (target.region === "PRG_ROM")
            this.armedRomOffsets.add(target.offset);
        else if (target.region === "CPU")
            this.armedCpuAddrs.add(target.offset);
        // PRG_RAM hooks are accepted but never fire (code rarely runs from
        // cart RAM); they sit inert until cleared, matching the NES core.
        this.installExecTrap();
        return id;
    }
    clearExecHook(handle) {
        const hook = this.execHooks.get(handle);
        if (!hook)
            return;
        this.execHooks.delete(handle);
        const stillArmed = (region, offset) => {
            for (const h of this.execHooks.values()) {
                if (h.region === region && h.offset === offset)
                    return true;
            }
            return false;
        };
        if (hook.region === "PRG_ROM" && !stillArmed("PRG_ROM", hook.offset)) {
            this.armedRomOffsets.delete(hook.offset);
        }
        else if (hook.region === "CPU" && !stillArmed("CPU", hook.offset)) {
            this.armedCpuAddrs.delete(hook.offset);
        }
        if (this.execHooks.size === 0)
            this.uninstallExecTrap();
    }
    translatePC(cpuAddr) {
        const a = cpuAddr & 0xffff;
        if (a < 0x8000)
            return { region: "PRG_ROM", offset: this.gb.bus.cartridge.romOffsetForAddress(a) };
        return { region: "CPU", offset: a };
    }
    // CPU register access. The CpuReg ABI is 6502-shaped (A/X/Y/SP/P/PC);
    // the LR35902 has no X/Y, so those map to nothing (read 0, write
    // ignored). A/SP/PC map directly; P maps to the flags register F — the
    // closest analogue. PC/SP are 16-bit, the rest 8-bit.
    readCpuReg(reg) {
        const r = this.gb.cpu.registers;
        switch (reg) {
            case CpuReg.A: return r.A.value & 0xff;
            case CpuReg.P: return r.F.value & 0xff;
            case CpuReg.SP: return r.stackPointer.value & 0xffff;
            case CpuReg.PC: return r.programCounter.value & 0xffff;
            default: return 0; // X / Y: no GB register
        }
    }
    writeCpuReg(reg, val) {
        const r = this.gb.cpu.registers;
        switch (reg) {
            case CpuReg.A:
                r.A.value = val & 0xff;
                break;
            case CpuReg.P:
                r.F.value = val & 0xff;
                break;
            case CpuReg.SP:
                r.stackPointer.value = val & 0xffff;
                break;
            case CpuReg.PC:
                r.programCounter.value = val & 0xffff;
                break;
            default: break; // X / Y: no GB register
        }
    }
    writeRomByte(coord, val) {
        const cart = this.gb.bus.cartridge;
        if (coord.region === "PRG_ROM") {
            cart.patchRom(coord.offset, val);
        }
        else if (coord.region === "CPU" && (coord.offset & 0xffff) < 0x8000) {
            cart.patchRom(cart.romOffsetForAddress(coord.offset & 0xffff), val);
        }
        // PRG_RAM / non-ROM CPU coords are not part of the ROM image; ignored.
    }
    installExecTrap() {
        if (this.execTrapInstalled)
            return;
        this.gb.cpu.onExecute = (pc) => this.fireExecHooks(pc);
        this.execTrapInstalled = true;
    }
    uninstallExecTrap() {
        if (!this.execTrapInstalled)
            return;
        this.gb.cpu.onExecute = undefined;
        this.execTrapInstalled = false;
    }
    // Fires pre-step on every instruction while armed. Forward-translates
    // the PC to a flat ROM offset and fires any hook whose target matches.
    // Handlers are collected before invocation so a handler may
    // register/clear hooks (mutating the map) without disturbing dispatch.
    fireExecHooks(pc) {
        const a = pc & 0xffff;
        let romOff = -1;
        if (this.armedRomOffsets.size > 0 && a < 0x8000) {
            romOff = this.gb.bus.cartridge.romOffsetForAddress(a);
        }
        const romHit = romOff >= 0 && this.armedRomOffsets.has(romOff);
        const cpuHit = this.armedCpuAddrs.size > 0 && this.armedCpuAddrs.has(a);
        if (!romHit && !cpuHit)
            return;
        let matched = null;
        for (const hook of this.execHooks.values()) {
            const hit = hook.region === "CPU"
                ? (cpuHit && hook.offset === a)
                : (romHit && hook.region === "PRG_ROM" && hook.offset === romOff);
            if (hit)
                (matched ??= []).push({ handler: hook.handler, priority: hook.priority });
        }
        if (matched) {
            // Ascending priority => higher fires later; stable sort + registration
            // order keeps equal priorities in place. Mirrors the NES core. Only sort
            // when >1 hook shares this instruction.
            if (matched.length > 1)
                matched.sort((x, y) => x.priority - y.priority);
            for (const m of matched)
                m.handler(pc);
        }
    }
    // ----- hooks -----
    onOamDmaWrite(handler) {
        this.oamDmaHandler = handler;
    }
    // ----- CPU memory -----
    //
    // Reads go through bus.readByte (whole memory map: cartridge, VRAM,
    // OAM, registers). Writes are limited to Work RAM at $C000–$DFFF;
    // writes outside are dropped. This is a contract requirement, not
    // just hygiene: a passthrough write to $FF46 would trigger a real
    // OAM-DMA *and* fire the host's onOamDmaWrite hook (the bus wrapper
    // above intercepts adapter-originated writes too), letting a Munos
    // RAM-poke inject a spurious render event.
    readCpuByte(addr) {
        return (this.gb.bus.readByte(addr & 0xffff) ?? 0) & 0xff;
    }
    writeCpuByte(addr, val) {
        const a = addr & 0xffff;
        // Full CPU-bus passthrough: write_u8 does exactly what the SM83 does when
        // it stores to this address, with all side effects. RAM regions ($A000-
        // $DFFF cart-RAM + WRAM, $FF80-$FFFE HRAM) land as plain bytes; I/O
        // registers ($FF00-$FF7F, incl. $FF46 OAM-DMA), the interrupt-enable
        // register ($FFFF IE), VRAM ($8000-$9FFF), and the $0000-$7FFF MBC/mapper
        // window (bank switching, RAM-enable) all fire their real effects. A cart
        // with no RAM ignores $A000-$BFFF, exactly as on hardware. Editing the
        // cartridge image itself is a different operation — see writeRomByte.
        this.gb.bus.writeByte(a, val & 0xff);
    }
    readRamSize() { return 0x4000; }
    // 16KB snapshot of the contiguous CPU RAM span $A000-$DFFF: cartridge RAM
    // ($A000-$BFFF, the active MBC bank as the running game sees it) followed by
    // internal Work RAM ($C000-$DFFF). Index 0 of the snapshot is $A000 (the
    // rig's ramBase for GB), so a spec/discriminator expression reading actor
    // state in cart RAM (e.g. Wario's $A10F) resolves correctly. Cart RAM is
    // captured via the live bus, so it reflects the active bank; carts with no
    // RAM read back as open-bus (harmless — no spec references absent RAM).
    // Returns a fresh copy so callers can't mutate emulator state.
    readRamSnapshot() {
        const ram = new Uint8Array(0x4000);
        for (let i = 0; i < ram.length; i++) {
            ram[i] = (this.gb.bus.readByte(0xa000 + i) ?? 0) & 0xff;
        }
        return ram;
    }
    // ----- OAM -----
    readOam() {
        return new Uint8Array(this.gb.gpu.oam);
    }
    // ----- PPU introspection (sprite identity) -----
    //
    // Shared by both tiers: the devtools panel (web) and the fidelity rig
    // (headless) both read sprite tiles + palettes. The background readers
    // stay browser-only (in ./web.ts) — no headless consumer renders
    // backgrounds, matching the NES split in core-contract.
    // Extract the 4 raw shade tokens (0..3) a GB palette register
    // (BGP / OBP0 / OBP1) maps each of its 4 palette-indices to.
    // Bits [1:0]=index 0, [3:2]=1, [5:4]=2, [7:6]=3. These shade tokens
    // ARE the console-native color identity (the GB analog of an NES
    // master-palette index): unlike the resolved 0x00BBGGRR they don't
    // depend on the SHADE_TABLE RGB the display happens to use, so the
    // fidelity rig keys color identity off them via the ColorModel.
    resolvePaletteIndices(palByte) {
        const out = new Uint8Array(4);
        out[0] = palByte & 0x03;
        out[1] = (palByte >> 2) & 0x03;
        out[2] = (palByte >> 4) & 0x03;
        out[3] = (palByte >> 6) & 0x03;
        return out;
    }
    // Resolve a GB palette register into 4 colors in the SHADE_TABLE
    // format. Derived from resolvePaletteIndices so the resolved RGB and
    // the raw shade tokens can never drift. Protected so ./web.ts's
    // background readers reuse it. For OBP, color 0 is always transparent
    // on sprites — callers treat it as such.
    resolvePalette(palByte) {
        const idx = this.resolvePaletteIndices(palByte);
        const out = new Uint32Array(4);
        for (let i = 0; i < 4; i++)
            out[i] = SHADE_TABLE[idx[i]];
        return out;
    }
    // LCDC bit 2: 0 = 8x8, 1 = 8x16. The vendor's objectHeight already
    // returns 8 or 16.
    readSpriteHeight() {
        return this.gb.gpu.lcdControl.objectHeight;
    }
    // 8-entry sprite palette: OBP0 in slots 0-3, OBP1 in slots 4-7. Slots
    // 0 and 4 are transparent (palette index 0 is transparent on sprites).
    readSpritePalette() {
        const obp0 = this.gb.gpu.objectPalettes[0].value & 0xff;
        const obp1 = this.gb.gpu.objectPalettes[1].value & 0xff;
        const out = new Uint32Array(8);
        out.set(this.resolvePalette(obp0), 0);
        out.set(this.resolvePalette(obp1), 4);
        return out;
    }
    // 8-entry sprite palette as raw DMG shade tokens (0..3): OBP0 in slots
    // 0-3, OBP1 in slots 4-7 — same layout as readSpritePalette. The
    // console-native, RGB-independent color identity (the GB analog of the
    // NES core's readSpritePaletteIndices master indices). Slots 0 and 4
    // mirror the transparent sprite slot; only opaque slots matter. Pass
    // each token through the GB ColorModel to compare identity cross-frame.
    readSpritePaletteIndices() {
        const obp0 = this.gb.gpu.objectPalettes[0].value & 0xff;
        const obp1 = this.gb.gpu.objectPalettes[1].value & 0xff;
        const out = new Uint8Array(8);
        out.set(this.resolvePaletteIndices(obp0), 0);
        out.set(this.resolvePaletteIndices(obp1), 4);
        return out;
    }
    // 8x8 tile pixels (palette indices 0..3, row-major). globalIndex
    // 0..383 = offset in 16-byte tile units from VRAM $8000. Returns a
    // fresh buffer; callers may not mutate emulator state.
    readTilePixels(globalIndex) {
        const out = new Uint8Array(64);
        if (globalIndex < 0 || globalIndex >= 384)
            return out;
        const vram = this.gb.gpu.vram;
        const base = globalIndex * 16;
        for (let row = 0; row < 8; row++) {
            const lo = vram[base + row * 2] ?? 0;
            const hi = vram[base + row * 2 + 1] ?? 0;
            for (let col = 0; col < 8; col++) {
                // GB pixel layout: bit 7 of each byte pair is the leftmost pixel.
                const shift = 7 - col;
                const bit0 = (lo >> shift) & 1;
                const bit1 = (hi >> shift) & 1;
                out[row * 8 + col] = (bit1 << 1) | bit0;
            }
        }
        return out;
    }
    // ----- PPU enable state -----
    // A game blanks the screen by clearing LCDC bit 7 (LCD enable) during
    // transitions and loads; while it is off the PPU outputs no pixels
    // regardless of the per-layer enable bits (the vendor's drawScanline
    // bails on the same condition). Gate both on the LCD-on bit so consumers
    // modelling "what the player sees" don't treat stale OAM as a live pose.
    isSpritesEnabled() {
        return this.gb.gpu.lcdControl.isLCDControllerOperating && this.gb.gpu.lcdControl.isObjOn;
    }
    isBackgroundEnabled() {
        return (this.gb.gpu.lcdControl.isLCDControllerOperating &&
            this.gb.gpu.lcdControl.isBackgroundDisplayOn);
    }
    // ----- savestate -----
    //
    // Implemented at adapter level (see ./savestate.ts) because the
    // vendored core has no serialization API. The envelope's `core`
    // field carries this core's registry id; the inner blob keeps its
    // own `__format = "gb-roblouie/v1"` version string, which
    // applyGbState re-checks. Either layer refusing means a NES
    // savestate fed in here surfaces a clear error
    // (SaveStateMismatchError at the envelope) instead of silently
    // corrupting machine state.
    saveState() {
        return { core: CORE_ID, data: captureGbState(this.gb) };
    }
    loadState(state) {
        applyGbState(this.gb, assertSaveStateCore(state, CORE_ID));
    }
    // ----- battery-backed save RAM (BatteryBackedCapable) -----
    //
    // The cartridge's save file: GB cart RAM ($A000-$BFFF, banked — up to 32 KB).
    // Serialized as the whole SRAM image (every bank) via the MBC's dumpRam, not
    // the single mapped $A000 window readRamSnapshot exposes, so a multi-bank save
    // round-trips intact. Returns null when the cart declares no battery, or its
    // MBC has no RAM dump (ROM-only / MBC2) — the host's signal to skip persistence.
    getBattery() {
        const cart = this.gb.bus.cartridge;
        if (!cart.hasSaveableRam || typeof cart.dumpRam !== "function")
            return null;
        return new Uint8Array(cart.dumpRam().slice(0));
    }
    setBattery(data) {
        const cart = this.gb.bus.cartridge;
        if (!cart.hasSaveableRam || typeof cart.setRam !== "function")
            return;
        // Only restore a save whose size matches this cart's SRAM region; a
        // mismatched/foreign blob is ignored rather than resized into corruption.
        if (data.byteLength !== cart.ramSize)
            return;
        cart.setRam(data.slice().buffer);
    }
}
//# sourceMappingURL=core-base.js.map